import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { deleteAdminUser, getApiErrorMessage, listAdminUsers, patchAdminUser, type AdminUser } from '../lib/api';
import { USERS_TEMPLATE } from '../templates/usersTemplate';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatLastLogin = (value: string | null): string => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const roleBadge = (role: AdminUser['role']): string => {
  if (role === 'admin') return '<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-600">Admin</span>';
  if (role === 'editor') return '<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-600">Editor</span>';
  return '<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600">User</span>';
};

const statusBadge = (isActive: boolean): string =>
  isActive
    ? '<span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-600"><span class="size-1.5 rounded-full bg-emerald-500"></span>Active</span>'
    : '<span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-rose-100 text-rose-600"><span class="size-1.5 rounded-full bg-rose-500"></span>Inactive</span>';

export default function UsersPage() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => USERS_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const searchInput = root.querySelector<HTMLInputElement>('#users-search-input');
    const statusFilter = root.querySelector<HTMLSelectElement>('#users-status-filter');
    const roleFilter = root.querySelector<HTMLSelectElement>('#users-role-filter');
    const orderingFilter = root.querySelector<HTMLSelectElement>('#users-ordering-filter');
    const tableBody = root.querySelector<HTMLTableSectionElement>('#users-table-body');
    const meta = root.querySelector<HTMLElement>('#users-table-meta');
    const reloadBtn = root.querySelector<HTMLButtonElement>('#users-reload-btn');
    const prevBtn = root.querySelector<HTMLButtonElement>('#users-prev-btn');
    const nextBtn = root.querySelector<HTMLButtonElement>('#users-next-btn');
    const pageIndicator = root.querySelector<HTMLElement>('#users-page-indicator');
    const modal = root.querySelector<HTMLElement>('#users-delete-modal');
    const modalBackdrop = root.querySelector<HTMLElement>('#users-delete-modal-backdrop');
    const modalCancel = root.querySelector<HTMLButtonElement>('#users-delete-modal-cancel');
    const modalConfirm = root.querySelector<HTMLButtonElement>('#users-delete-modal-confirm');

    if (
      !searchInput ||
      !statusFilter ||
      !roleFilter ||
      !orderingFilter ||
      !tableBody ||
      !meta ||
      !reloadBtn ||
      !prevBtn ||
      !nextBtn ||
      !pageIndicator ||
      !modal ||
      !modalBackdrop ||
      !modalCancel ||
      !modalConfirm
    ) {
      return;
    }

    let disposed = false;
    let searchTimer: number | null = null;
    let currentPage = 1;
    const pageSize = 20;

    const confirmDelete = (): Promise<boolean> =>
      new Promise((resolve) => {
        modal.classList.remove('hidden');

        const close = (result: boolean) => {
          modal.classList.add('hidden');
          modalCancel.removeEventListener('click', onCancel);
          modalConfirm.removeEventListener('click', onConfirm);
          modalBackdrop.removeEventListener('click', onCancel);
          resolve(result);
        };

        const onCancel = () => close(false);
        const onConfirm = () => close(true);

        modalCancel.addEventListener('click', onCancel);
        modalConfirm.addEventListener('click', onConfirm);
        modalBackdrop.addEventListener('click', onCancel);
      });

    const renderRows = (users: AdminUser[]) => {
      if (!users.length) {
        tableBody.innerHTML = '<tr><td class="px-6 py-6 text-sm text-slate-500" colspan="6">No users found.</td></tr>';
        return;
      }

      tableBody.innerHTML = users
        .map((user) => {
          const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
          const initials = fullName
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() ?? '')
            .join('') || 'U';
          const avatar = user.avatar
            ? `<img src="${escapeHtml(user.avatar)}" alt="${escapeHtml(fullName)}" class="w-10 h-10 rounded-full object-cover ring-2 ring-slate-100 shrink-0" loading="lazy"/>`
            : `<div class="w-10 h-10 rounded-full bg-slate-200 ring-2 ring-slate-100 shrink-0 flex items-center justify-center text-xs font-bold text-slate-600">${escapeHtml(
                initials
              )}</div>`;
          return `<tr class="hover:bg-slate-50/70 transition-colors"><td class="px-6 py-4"><div class="flex items-center gap-3">${avatar}<span class="text-sm font-semibold text-slate-900">${escapeHtml(
            fullName
          )}</span></div></td><td class="px-6 py-4 text-sm text-slate-600">${escapeHtml(user.email)}</td><td class="px-6 py-4">${roleBadge(
            user.role
          )}</td><td class="px-6 py-4">${statusBadge(user.is_active)}</td><td class="px-6 py-4 text-sm text-slate-500">${formatLastLogin(
            user.last_login
          )}</td><td class="px-6 py-4"><div class="flex items-center justify-end gap-2"><button class="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-primary transition-colors" data-action="edit" data-id="${
            user.id
          }" type="button"><span class="material-symbols-outlined text-[20px]">edit</span></button><button class="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-amber-600 transition-colors" data-action="toggle" data-id="${
            user.id
          }" data-active="${String(user.is_active)}" type="button"><span class="material-symbols-outlined text-[20px]">${
            user.is_active ? 'block' : 'check_circle'
          }</span></button><button class="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-red-500 transition-colors" data-action="delete" data-id="${
            user.id
          }" type="button"><span class="material-symbols-outlined text-[20px]">delete</span></button></div></td></tr>`;
        })
        .join('');
    };

    const loadUsers = async (page = currentPage) => {
      currentPage = Math.max(1, page);
      tableBody.innerHTML = '<tr><td class="px-6 py-6 text-sm text-slate-500" colspan="6">Loading users...</td></tr>';
      try {
        const response = await listAdminUsers({
          search: searchInput.value.trim() || undefined,
          role: roleFilter.value as 'admin' | 'editor' | 'user' | 'all',
          is_active: statusFilter.value as 'true' | 'false' | 'all',
          ordering: orderingFilter.value,
          page: currentPage,
          page_size: pageSize,
        });
        if (disposed) return;
        renderRows(response.results);
        const from = response.count === 0 ? 0 : (currentPage - 1) * pageSize + 1;
        const to = response.count === 0 ? 0 : Math.min(from + response.results.length - 1, response.count);
        meta.textContent = `Showing ${from}-${to} of ${response.count} users`;
        pageIndicator.textContent = `Page ${currentPage}`;
        prevBtn.disabled = !response.previous;
        nextBtn.disabled = !response.next;
      } catch (error) {
        if (disposed) return;
        tableBody.innerHTML = `<tr><td class="px-6 py-6 text-sm text-rose-600" colspan="6">${escapeHtml(
          getApiErrorMessage(error, 'Failed to load users.')
        )}</td></tr>`;
        meta.textContent = 'Failed to load users';
        pageIndicator.textContent = `Page ${currentPage}`;
        prevBtn.disabled = true;
        nextBtn.disabled = true;
      }
    };

    const onSearch = () => {
      if (searchTimer) window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        currentPage = 1;
        void loadUsers(1);
      }, 250);
    };

    const onTableClick = async (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest<HTMLButtonElement>('button[data-action][data-id]');
      if (!button) return;

      const action = button.dataset.action;
      const id = Number(button.dataset.id);
      if (!Number.isFinite(id) || id <= 0) return;

      if (action === 'edit') {
        navigate(`/users/create?id=${id}`);
        return;
      }

      if (action === 'delete') {
        let ok = false;
        try {
          ok = await confirmDelete();
        } catch {
          ok = window.confirm('Delete this user? This action cannot be undone.');
        }
        if (!ok) return;
      }

      button.disabled = true;
      try {
        if (action === 'toggle') {
          const isActive = button.dataset.active === 'true';
          await patchAdminUser(id, { is_active: !isActive });
        }

        if (action === 'delete') {
          await deleteAdminUser(id);
        }

        await loadUsers();
      } catch (error) {
        tableBody.insertAdjacentHTML(
          'afterbegin',
          `<tr><td class="px-6 py-3 text-xs text-rose-600" colspan="6">${escapeHtml(
            getApiErrorMessage(error, 'User action failed.')
          )}</td></tr>`
        );
        button.disabled = false;
      }
    };

    searchInput.addEventListener('input', onSearch);
    statusFilter.addEventListener('change', () => {
      currentPage = 1;
      void loadUsers(1);
    });
    roleFilter.addEventListener('change', () => {
      currentPage = 1;
      void loadUsers(1);
    });
    orderingFilter.addEventListener('change', () => {
      currentPage = 1;
      void loadUsers(1);
    });
    reloadBtn.addEventListener('click', () => {
      void loadUsers();
    });
    prevBtn.addEventListener('click', () => {
      if (currentPage <= 1) return;
      void loadUsers(currentPage - 1);
    });
    nextBtn.addEventListener('click', () => {
      void loadUsers(currentPage + 1);
    });
    tableBody.addEventListener('click', onTableClick);

    void loadUsers();

    return () => {
      disposed = true;
      if (searchTimer) window.clearTimeout(searchTimer);
      searchInput.removeEventListener('input', onSearch);
      tableBody.removeEventListener('click', onTableClick);
    };
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Users</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen" ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
    </>
  );
}
